import type { PublishGlobalOnlyChange } from "@repo/app-types/helpers/playbookNodePublish";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { type FC, useState } from "react";
import { PublishSettingConfirmationModal } from "./publishPlaybookPartialChangeConfirmationModal.js";

export interface PublishSettingButtonProps {
  settingLabel: "outcome" | "summary template";
  hasChangesToPublish: boolean;
  isPublishBlocked?: boolean;
  /** Explains an additional disabled state after changes exist. */
  disabledReason?: string;
  isPublishing?: boolean;
  globalOnlyChanges: readonly PublishGlobalOnlyChange[];
  onPublish: () => void;
}

/** Inline setting publish action with the same disabled tooltip as node publishing. */
export const PublishSettingButton: FC<PublishSettingButtonProps> = (props) => {
  const {
    settingLabel,
    hasChangesToPublish,
    isPublishBlocked = false,
    disabledReason,
    isPublishing = false,
    globalOnlyChanges,
    onPublish,
  } = props;
  const [showConfirmation, setShowConfirmation] = useState(false);
  const needsConfirmation = globalOnlyChanges.length > 0;
  const disabledTooltip = disabledReason ?? "No changes to publish";
  const hasDisabledTooltip = !hasChangesToPublish || !!disabledReason;

  return (
    <>
      <TooltipTrigger isDisabled={!hasDisabledTooltip}>
        <TooltipHoverTarget shape="rounded" isDisabled={!hasDisabledTooltip}>
          <Button
            variant={ButtonVariant.Primary}
            size={Size.MEDIUM}
            onClick={
              needsConfirmation ? () => setShowConfirmation(true) : onPublish
            }
            isDisabled={isPublishBlocked || !hasChangesToPublish}
            isLoading={isPublishing}
          >
            Publish this {settingLabel}
          </Button>
        </TooltipHoverTarget>
        <SimpleTooltip>{disabledTooltip}</SimpleTooltip>
      </TooltipTrigger>
      <PublishSettingConfirmationModal
        isOpen={showConfirmation}
        onOpenChange={setShowConfirmation}
        settingLabel={settingLabel}
        globalOnlyChanges={globalOnlyChanges}
        onConfirm={onPublish}
      />
    </>
  );
};
